import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { Building2, Save } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  dealership_name: "",
  dealership_address: "",
  contact_name: "",
  phone: "",
  email: "",
};

export default function PartnerProfile() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    api.get("/partner/profile")
      .then(({ data }) => setForm({ ...EMPTY, ...data, contact_name: data.contact_name || data.name || "" }))
      .catch(() => toast.error("Could not load dealer profile"))
      .finally(() => setLoading(false));
  }, []);

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/partner/profile", form);
      setForm({ ...EMPTY, ...data, contact_name: data.contact_name || data.name || "" });
      toast.success("Dealer profile updated");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || "Could not update dealer profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Navbar />
      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-md bg-indigo-50 text-indigo-700">
            <Building2 className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-indigo-700">Partner dealer</p>
            <h1 className="font-display text-3xl font-bold text-slate-950 dark:text-slate-50">Dealer profile</h1>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">Keep the dealership and primary contact details current for future broker use.</p>
          </div>
        </div>
        <form onSubmit={save} className="mt-8 border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900" data-testid="partner-dealer-profile-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label htmlFor="partner-profile-dealer">Dealer name</Label><Input id="partner-profile-dealer" data-testid="partner-profile-dealer-name" required value={form.dealership_name} onChange={(event) => set("dealership_name", event.target.value)} className="mt-1.5" /></div>
            <div><Label htmlFor="partner-profile-contact">Contact person</Label><Input id="partner-profile-contact" data-testid="partner-profile-contact-person" required value={form.contact_name} onChange={(event) => set("contact_name", event.target.value)} className="mt-1.5" /></div>
          </div>
          <div className="mt-4"><Label htmlFor="partner-profile-address">Dealer address</Label><Input id="partner-profile-address" data-testid="partner-profile-address" required value={form.dealership_address} onChange={(event) => set("dealership_address", event.target.value)} className="mt-1.5" placeholder="Street, city, state ZIP" /></div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div><Label htmlFor="partner-profile-phone">Phone number</Label><Input id="partner-profile-phone" data-testid="partner-profile-phone" required value={form.phone} onChange={(event) => set("phone", event.target.value)} className="mt-1.5" /></div>
            <div><Label htmlFor="partner-profile-email">Email address</Label><Input id="partner-profile-email" data-testid="partner-profile-email" type="email" required value={form.email} onChange={(event) => set("email", event.target.value)} className="mt-1.5" /></div>
          </div>
          <Button type="submit" disabled={loading || saving} data-testid="partner-profile-save" className="mt-6 w-full rounded-md bg-indigo-700 font-semibold hover:bg-indigo-800"><Save className="mr-2 h-4 w-4" />{saving ? "Saving…" : "Save dealer profile"}</Button>
        </form>
      </main>
    </div>
  );
}